import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { 
  X, 
  Info,
  Sparkles,
  Loader2,
} from 'lucide-react';
import api from '@/api';
import axios from 'axios';
import { EmojiPicker } from '../ui/EmojiPicker';
import { useExtensionUtil } from '@/hooks/useExtensionUtil';
import { useDialogA11y } from '@/hooks/useDialogA11y';
import { useEntitlements } from '@/hooks/useEntitlements';
import { SessionContextRulesFields } from '../sessions/SessionContextRulesFields';
import { AutoSuspendSessionFields } from '../sessions/AutoSuspendSessionFields';
import type { SessionContextRules } from '@/types/session-context';
import {
  DEFAULT_AUTO_SUSPEND_CONFIG,
  type AutoSuspendConfig,
} from '@/types/auto-suspend';
import { useSessionsStore } from '@/stores/sessions-store';
import { notifications } from '@/utils/notifications';
import { AiSuggestionRow } from '@/components/ai/AiSuggestionRow';
import { fetchWorkMemoryGates } from '@/api/activity';

interface EditTabsGroupModalProps {
  isOpen: boolean;
  onClose: () => void;
  /** Called after a successful save so the parent can sync local state (e.g. sidebar session list). */
  onSuccess: (result: {
    name: string;
    icon: string;
    closeTabsOnSessionChange: boolean;
    autoSuspend: AutoSuspendConfig;
  }) => void;
  sessionId: string;
  section: 'PROJECTS' | 'UNSAVED';
  initialName?: string;
  initialIcon?: string;
  initialContextRules?: SessionContextRules;
  initialCloseTabsOnSessionChange?: boolean;
  initialAutoSuspend?: Partial<AutoSuspendConfig>;
}

export const EditTabsGroupModal: React.FC<EditTabsGroupModalProps> = ({ 
  isOpen, 
  onClose, 
  onSuccess,
  sessionId,
  section,
  initialName = '',
  initialIcon = '📁',
  initialContextRules,
  initialCloseTabsOnSessionChange = false,
  initialAutoSuspend,
}) => {
  const [name, setName] = useState(initialName);
  const [icon, setIcon] = useState(initialIcon);
  const [contextRules, setContextRules] = useState<SessionContextRules>(
    initialContextRules ?? { strictMode: section === 'PROJECTS' }
  );
  const [closeTabsOnSessionChange, setCloseTabsOnSessionChange] = useState(
    initialCloseTabsOnSessionChange
  );
  const [autoSuspend, setAutoSuspend] = useState<AutoSuspendConfig>({
    ...DEFAULT_AUTO_SUSPEND_CONFIG,
    ...initialAutoSuspend,
    excludeDomains: initialAutoSuspend?.excludeDomains ?? [],
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showEmojiPicker, setShowEmojiPicker] = useState(false);
  const pickerRef = useRef<HTMLDivElement>(null);
  const dialogRootRef = useRef<HTMLDivElement | null>(null);
  const { sendMessage } = useExtensionUtil();
  const { allSessions, sessionUpdate } = useSessionsStore();
  const { effectivePlanId } = useEntitlements();
  const { t } = useTranslation('common');
  const pendingNameSuggestRef = useRef(false);
  const pendingThemeSuggestRef = useRef(false);
  const [nameSuggestions, setNameSuggestions] = useState<string[]>([]);
  const [nameSuggestLoading, setNameSuggestLoading] = useState(false);
  const [wave2Enabled, setWave2Enabled] = useState(false);
  const [themeSuggestion, setThemeSuggestion] = useState<{
    aiTheme: string;
    aiSummary: string;
    aiConfidence: number;
  } | null>(null);
  const [themeSuggestLoading, setThemeSuggestLoading] = useState(false);

  const domainConflicts = useMemo(() => {
    const hosts = new Set(
      (contextRules.domains ?? [])
        .map(d => d.host.trim().toLowerCase().replace(/^www\./, ''))
        .filter(Boolean)
    );
    const conflicts: Array<{ host: string; otherSessionName: string }> = [];
    for (const sess of allSessions) {
      if (sess.sid === sessionId) continue;
      const rules = (sess as { contextRules?: SessionContextRules }).contextRules;
      for (const rule of rules?.domains ?? []) {
        const h = rule.host.trim().toLowerCase().replace(/^www\./, '');
        if (hosts.has(h)) {
          conflicts.push({ host: h, otherSessionName: sess.name });
        }
      }
    }
    return conflicts;
  }, [allSessions, contextRules.domains, sessionId]);

  useDialogA11y(dialogRootRef, { isOpen, onClose, disabled: showEmojiPicker });

  // Click outside handler for emoji picker
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (pickerRef.current && !pickerRef.current.contains(event.target as Node)) {
        setShowEmojiPicker(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Reset form when modal opens with new data
  useEffect(() => {
    if (isOpen) {
      setName(initialName);
      setIcon(initialIcon);
      setContextRules(initialContextRules ?? { strictMode: section === 'PROJECTS' });
      setCloseTabsOnSessionChange(initialCloseTabsOnSessionChange);
      setAutoSuspend({
        ...DEFAULT_AUTO_SUSPEND_CONFIG,
        ...initialAutoSuspend,
        excludeDomains: initialAutoSuspend?.excludeDomains ?? [],
      });
      setError(null);
      setShowEmojiPicker(false);
      setIsSubmitting(false);
      setNameSuggestions([]);
      setNameSuggestLoading(false);
      setThemeSuggestion(null);
      setThemeSuggestLoading(false);
    }
  }, [isOpen, initialName, initialIcon, initialContextRules, initialCloseTabsOnSessionChange, initialAutoSuspend, section]);

  useEffect(() => {
    if (!isOpen) return;
    void fetchWorkMemoryGates().then((g) => {
      if (g?.wave2AiThemesEnabled) setWave2Enabled(true);
    });
  }, [isOpen]);

  const fetchNameSuggestions = useCallback(
    async (sessionName: string, tabs: Array<{ title: string; host: string }>) => {
      if (!tabs.length) {
        notifications.warning(t('sessions.nameSuggest.noTabs'));
        return;
      }
      setNameSuggestLoading(true);
      setNameSuggestions([]);
      try {
        const response = await api.post<{ status: string; suggestions?: string[]; message?: string }>(
          '/ai/session/suggest-name',
          { sessionName, tabs }
        );
        if (response.data.status !== 'success' || !response.data.suggestions?.length) {
          throw new Error(response.data.message ?? t('sessions.nameSuggest.failed'));
        }
        setNameSuggestions(response.data.suggestions);
        notifications.success(t('sessions.nameSuggest.pickHint'));
      } catch (err) {
        let message = t('sessions.nameSuggest.failed');
        if (axios.isAxiosError(err)) {
          if (err.response?.status === 402) {
            message = t('sessions.nameSuggest.upgrade');
          } else {
            message = err.response?.data?.message ?? message;
          }
        } else if (err instanceof Error && err.message) {
          message = err.message;
        }
        notifications.error(message);
      } finally {
        setNameSuggestLoading(false);
      }
    },
    [t]
  );

  useEffect(() => {
    const update = sessionUpdate as Record<string, unknown> | undefined;
    if (!update?.type || update.sid !== sessionId) return;
    if (pendingNameSuggestRef.current && update.type === 'sessionContextTabSamples') {
      pendingNameSuggestRef.current = false;
      const tabs = (update.tabs as Array<{ title: string; host: string }> | undefined) ?? [];
      void fetchNameSuggestions(String(update.sessionName ?? name), tabs);
    }
  }, [sessionUpdate, sessionId, name, fetchNameSuggestions]);

  const handleSuggestName = () => {
    if (effectivePlanId === 0) {
      notifications.info(t('sessions.nameSuggest.upgrade'));
      return;
    }
    pendingNameSuggestRef.current = true;
    sendMessage('tabMgm', { action: 'sessionContextTabSamples', sid: sessionId });
  };

  const handleSuggestTheme = () => {
    if (effectivePlanId === 0) {
      notifications.info(t('sessions.nameSuggest.upgrade'));
      return;
    }
    if (!wave2Enabled) {
      notifications.info(t('sessions.themeSuggest.gated'));
      return;
    }
    pendingThemeSuggestRef.current = true;
    sendMessage('tabMgm', { action: 'sessionContextTabSamples', sid: sessionId });
    setThemeSuggestLoading(true);
  };

  useEffect(() => {
    const update = sessionUpdate as Record<string, unknown> | undefined;
    if (!update?.type || update.sid !== sessionId) return;
    if (pendingThemeSuggestRef.current && update.type === 'sessionContextTabSamples') {
      pendingThemeSuggestRef.current = false;
      const tabs = (update.tabs as Array<{ title: string; host: string }> | undefined) ?? [];
      void (async () => {
        try {
          const response = await api.post<{
            status: string;
            suggestion?: { aiTheme: string; aiSummary: string; aiConfidence: number };
            message?: string;
          }>('/ai/session/suggest-theme', { sessionName: name, tabs });
          if (response.data.status === 'success' && response.data.suggestion) {
            setThemeSuggestion(response.data.suggestion);
          } else {
            throw new Error(response.data.message ?? 'Theme suggest failed');
          }
        } catch (err) {
          if (axios.isAxiosError(err) && err.response?.status === 403) {
            notifications.info(t('sessions.themeSuggest.gated'));
          } else {
            notifications.error(t('sessions.themeSuggest.failed'));
          }
        } finally {
          setThemeSuggestLoading(false);
        }
      })();
    }
  }, [sessionUpdate, sessionId, name, t]);

  if (!isOpen) return null;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!name.trim()) {
      setError('Name is required');
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);

      const result = {
        name: name.trim(),
        icon,
        closeTabsOnSessionChange,
        autoSuspend,
      };

      if (section === 'UNSAVED') {
        sendMessage('tabMgm', {
          action: 'updatename',
          sid: sessionId,
          name: result.name,
          icon,
          contextRules,
          closeTabsOnSessionChange,
          autoSuspend,
        });
        setIsSubmitting(false);
        onSuccess(result);
        onClose();
      } else {
        // Update via API for PROJECTS (body id, not path — see session.routes)
        const response = await api.put('/sessions', {
          id: sessionId,
          name: result.name,
          icon,
          contextRules,
          closeTabsOnSessionChange,
          autoSuspend,
        });

        if (response.data.status === 'success') {
          sendMessage('tabMgm', {
            action: 'updatename',
            sid: sessionId,
            name: result.name,
            icon,
            contextRules,
            closeTabsOnSessionChange,
            autoSuspend,
          });
          setIsSubmitting(false);
          onSuccess(result);
          onClose();
        } else {
          throw new Error(response.data.message || 'Failed to update workspace');
        }
      }
    } catch (err: unknown) {
      console.error('Failed to update workspace:', err);
      let message = 'Failed to update workspace. Please try again.';
      
      if (axios.isAxiosError(err)) {
        message = err.response?.data?.message || message;
      } else if (err instanceof Error) {
        message = err.message;
      }
      
      setError(message);
      setIsSubmitting(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[150] flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/70 backdrop-blur-sm transition-opacity"
        onClick={onClose}
      />

      {/* Modal Content */}
      <div
        ref={dialogRootRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-tabs-group-modal-title"
        tabIndex={-1}
        className="relative flex max-h-[min(92dvh,calc(100vh-1.5rem))] w-full max-w-xl min-w-0 flex-col overflow-hidden bg-background modal-surface border border-border shadow-modal animate-in fade-in zoom-in duration-200 focus:outline-none"
      >

        {/* Header */}
        <div className="flex shrink-0 items-center justify-between border-b border-border-muted px-8 py-6">
          <h2 id="edit-tabs-group-modal-title" className="text-xl font-bold text-foreground italic">
            {t('shared.modals.editWorkspace.title')}
          </h2>
          <button
            type="button"
            onClick={onClose}
            aria-label={t('common.close')}
            className="p-2 text-muted-foreground/60 hover:text-foreground hover:bg-muted/50 rounded-xl transition-all"
          >
            <X size={20} aria-hidden />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="flex min-h-0 flex-1 flex-col overflow-hidden">
          <div className="min-h-0 flex-1 space-y-6 overflow-y-auto p-8">
          {error && (
            <div className="p-3 rounded-xl bg-destructive/10 border border-destructive/20 text-destructive text-xs flex items-start gap-2">
              <Info size={14} className="mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          <>
              {/* Workspace Name */}
              <div className="space-y-3 relative">
                <div className="flex items-center justify-between px-1">
                  <label className="text-[11px] font-bold text-muted-foreground/60 uppercase tracking-widest">
                    {t('shared.modals.editWorkspace.nameLabel')} <span className="text-destructive ml-0.5">*</span>
                  </label>
                  <button
                    type="button"
                    onClick={handleSuggestName}
                    disabled={nameSuggestLoading}
                    className="inline-flex items-center gap-1.5 rounded-lg border border-primary/25 bg-primary/5 px-2 py-1 text-[10px] font-bold text-primary hover:bg-primary/10 disabled:opacity-50"
                  >
                    {nameSuggestLoading ? (
                      <Loader2 size={12} className="animate-spin" />
                    ) : (
                      <Sparkles size={12} />
                    )}
                    {nameSuggestLoading
                      ? t('sessions.nameSuggest.loading')
                      : t('sessions.nameSuggest.button')}
                  </button>
                </div>
                <div className="relative group">
                  <button
                    type="button"
                    onClick={() => setShowEmojiPicker(!showEmojiPicker)}
                    className="absolute left-2 top-1/2 -translate-y-1/2 w-10 h-10 flex items-center justify-center text-xl bg-muted/50 hover:bg-muted rounded-xl border border-border transition-all active:scale-90 z-10"
                  >
                    {icon}
                  </button>
                  <input 
                    autoFocus
                    type="text"
                    placeholder={t('shared.modals.editWorkspace.namePlaceholder')}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="w-full bg-muted/30 border border-primary/30 rounded-2xl py-3.5 pl-14 pr-4 text-sm text-foreground placeholder:text-muted-foreground/40 focus:outline-none focus:ring-4 focus:ring-primary/10 transition-all font-medium"
                  />
                </div>

                {showEmojiPicker && (
                  <div 
                    ref={pickerRef}
                    className="dropdown-menu-panel absolute left-0 top-[calc(100%+8px)] z-[160] animate-in fade-in slide-in-from-top-2 duration-200"
                  >
                    <EmojiPicker 
                      onSelect={(emoji) => {
                        setIcon(emoji);
                        setShowEmojiPicker(false);
                      }} 
                    />
                  </div>
                )}

                {nameSuggestions.length > 0 && (
                  <div className="flex flex-wrap gap-2 px-1">
                    {nameSuggestions.map((suggestion) => (
                      <button
                        key={suggestion}
                        type="button"
                        onClick={() => setName(suggestion)}
                        className="rounded-full border border-border bg-muted/30 px-3 py-1 text-xs font-semibold text-foreground hover:bg-muted/60"
                      >
                        {suggestion}
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {section === 'PROJECTS' && wave2Enabled ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between px-1">
                    <label className="text-[11px] font-bold text-muted-foreground/60 uppercase tracking-widest">
                      {t('sessions.themeSuggest.label')}
                    </label>
                    <button
                      type="button"
                      onClick={handleSuggestTheme}
                      disabled={themeSuggestLoading}
                      className="inline-flex items-center gap-1.5 rounded-lg border border-primary/25 bg-primary/5 px-2 py-1 text-[10px] font-bold text-primary hover:bg-primary/10 disabled:opacity-50"
                    >
                      {themeSuggestLoading ? (
                        <Loader2 size={12} className="animate-spin" />
                      ) : (
                        <Sparkles size={12} />
                      )}
                      {t('sessions.themeSuggest.button')}
                    </button>
                  </div>
                  {themeSuggestion ? (
                    <AiSuggestionRow
                      label={themeSuggestion.aiTheme}
                      description={themeSuggestion.aiSummary}
                      loading={isSubmitting}
                      onAccept={() => {
                        void (async () => {
                          try {
                            setIsSubmitting(true);
                            await api.put('/sessions', {
                              id: sessionId,
                              aiTheme: themeSuggestion.aiTheme,
                              aiSummary: themeSuggestion.aiSummary,
                              aiConfidence: themeSuggestion.aiConfidence,
                            });
                            notifications.success(t('sessions.themeSuggest.accepted'));
                            setThemeSuggestion(null);
                          } catch {
                            notifications.error(t('sessions.themeSuggest.failed'));
                          } finally {
                            setIsSubmitting(false);
                          }
                        })();
                      }}
                      onDismiss={() => setThemeSuggestion(null)}
                    />
                  ) : null}
                </div>
              ) : null}

              <SessionContextRulesFields
                value={contextRules}
                onChange={setContextRules}
                syncScope={section === 'PROJECTS' ? 'workspace' : 'local'}
                domainConflicts={domainConflicts}
                sessionId={sessionId}
                sessionName={name}
              />

              <div className="space-y-3 rounded-2xl border border-border-muted bg-muted/20 px-4 py-4">
                <p className="text-[11px] font-bold text-muted-foreground/60 uppercase tracking-widest px-1">
                  {t('sessions.memory.title')}
                </p>
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-sm font-medium text-foreground">
                      {t('sessions.memory.closeTabsOnSwitchLabel')}
                    </p>
                    <p className="mt-1 text-xs leading-relaxed text-muted-foreground">
                      {t('sessions.memory.closeTabsOnSwitchDescription')}
                    </p>
                  </div>
                  <button
                    type="button"
                    role="switch"
                    aria-checked={closeTabsOnSessionChange}
                    aria-label={t('sessions.memory.closeTabsOnSwitchLabel')}
                    onClick={() => setCloseTabsOnSessionChange((prev) => !prev)}
                    className={
                      'relative inline-flex h-6 w-11 shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors focus:outline-none focus:ring-2 focus:ring-primary ' +
                      (closeTabsOnSessionChange ? 'bg-primary' : 'bg-muted')
                    }
                  >
                    <span
                      aria-hidden
                      className={
                        'pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition ' +
                        (closeTabsOnSessionChange ? 'translate-x-5' : 'translate-x-0')
                      }
                    />
                  </button>
                </div>
              </div>

              <AutoSuspendSessionFields value={autoSuspend} onChange={setAutoSuspend} />
            </>
          </div>

          {/* Footer Actions */}
          <div className="flex shrink-0 items-center justify-end gap-3 border-t border-border-muted px-8 py-5">
            <button 
              type="button"
              onClick={onClose}
              className="px-6 py-2.5 text-sm font-bold text-muted-foreground/60 hover:text-foreground hover:bg-muted border border-border rounded-2xl transition-all"
            >
              {t('shared.modals.editWorkspace.cancel')}
            </button>
            <button 
              disabled={isSubmitting}
              type="submit"
              className="px-8 py-2.5 bg-primary hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed text-white font-bold rounded-2xl text-sm active:scale-95 transition-all"
            >
              {isSubmitting ? t('shared.modals.editWorkspace.saving') : t('shared.modals.editWorkspace.save')}
            </button>
          </div>
        </form>

      </div>
    </div>
  );
};
